import { Autocomplete, Box, Button, Chip, FormControl, Grid, SvgIcon, TextField, useTheme } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';

import useNswagClient from '../../hooks/api/useNswagClient';
import { UserContext } from '../../components/shared/useUser';
import { ConversionDto } from '../../app/services/api/generated';
import { UomConversionsTable } from './components/UomConversionsTable';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { useParams } from 'react-router-dom';
import { SearchSm } from '../../assets';
import { Section } from '../../components/ui/Section';
import { handleSearchFieldKeyDown } from '../../utils';

export default function UomConversions() {
  const { getConversions } = useNswagClient();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [uomClass, setUomClass] = useState<string[]>([]);
  const [data, setData] = useState<ConversionDto[]>([]);
  const [page, setPage] = useState(0);
  const [maxPage, setMaxPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('uomConversions');
  const { itemNumber } = useParams();

  useEffect(() => {
    if (itemNumber) {
      handleAutomaticSearch();
    }
    else {
      loadData();
    }
  }, []);

  const uom_class = [
    { id: 'case', display: 'Case' },
    { id: 'eu pallet', display: 'EU Pallet' },
    { id: 'liquid', display: 'Liquid' },
    { id: 'pallet', display: 'Pallet' },
    { id: 'quantity', display: 'Quantity' },
    { id: 'uk pallet', display: 'UK Pallet' },
    { id: 'weight', display: 'Weight' },
  ];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toUpperCase());
  };

  const handleAutomaticSearch = () => {
    setSearchTerm(itemNumber!.toString());
    setPage(0);
    loadData(itemNumber!.toString(), 0);
  };

  const handleTypeChange = (event: React.SyntheticEvent, value: string[]) => {
    setUomClass(value);
    setPage(0);
  };

  const handlePageChange = (passedPage: number) => {
    setPage(passedPage);
    loadData(searchTerm, passedPage);
  };

  const handleClearClick = () => {
    setSearchTerm('');
    setUomClass([]);
    setPage(0);
    loadData('', 0, '');
  };

  const loadData = (search?: string, passedPage?: number, passedClass?: string) => {
    setIsLoading(true);
    const searchToUse = search ?? searchTerm;
    const pageToUse = passedPage ?? page;
    let uomClassToUse = passedClass;
    if (uomClassToUse == undefined) {
      if (uomClassToUse != '') {
        const final = uomClass.length === 1 ? uomClass[0] : uomClass.join(',');
        uomClassToUse = final;
      }
    }

    getConversions(searchToUse, uomClassToUse, pageToUse * 10, 10, selectedStore?.storeNumber).then(
      (result) => {
        if (result) {
          const total = result.conversionsCount ?? 0;
          setMaxPage(Math.ceil(total / 10));
          setData(result.conversions ?? []);
          setTotalItems(total);
        }
        else {
          setMaxPage(0);
          setData([]);
          setTotalItems(0);
        }
      },
    )
      .finally(() => {
        setIsLoading(false);
        setLastSearchTerm(searchToUse);
      });
  };

  const HandelSearchClicked = () => {
    setPage(0);
    loadData(undefined, 0, undefined);
  };

  return (
    <PageArea>
      <PageTitle subTitle={t('subTitle')}>{t('title')}</PageTitle>
      <Box sx={{ pt: '6px' }} >
        <Grid container
          spacing={5}>
          <Grid item
            lg={4}
            md={6}
            sm={6}
            xs={12}>
            <FormControl fullWidth
              size='small'>
              <TextField id="outlined-basic"
                label={t('search')}
                size='small'
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={e => handleSearchFieldKeyDown(e, HandelSearchClicked)} />
            </FormControl>
          </Grid>
          <Grid item
            lg={4}
            md={6}
            sm={6}
            xs={12}>

            <Autocomplete
              multiple
              options={uom_class.map(c => c.id)}
              getOptionLabel={(option) => uom_class.find(c => c.id === option)?.display ?? ''}
              value={uomClass}
              onChange={handleTypeChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  return (
                    // eslint-disable-next-line react/jsx-key
                    <Chip {...tagProps}      // spread the rest
                      variant="outlined"
                      size="small"
                      label={uom_class.find(c => c.id === option)?.display ?? ''}
                      sx={{ height: 24, fontSize: '0.75rem', my: 0 }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField {...params}
                  label={t('uomClass')}
                  variant="outlined" />
              )}
              sx={{
                '& .MuiAutocomplete-tag': {
                  marginTop: '2px', // reduce or eliminate vertical space
                  marginBottom: '2px',
                },
              }}
            />
          </Grid>
          <Grid item
            lg={2}
            md={3}
            sm={6}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <Button
              variant="secondary"
              size="lg"
              onClick={handleClearClick}
              fullWidth
            >
              Clear
            </Button>
          </Grid>

          <Grid item
            lg={2}
            md={3}
            sm={6}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
              onClick={HandelSearchClicked}
              fullWidth
            >
              Search
            </Button>
          </Grid>
          <Grid item
            xs={12}>
            <Section>
              <UomConversionsTable searchTerm={lastSearchTerm}
                handleClearClick={handleClearClick}
                page={page}
                maxPage={maxPage}
                handlePageChange={handlePageChange}
                data={data}
                totalItems={totalItems}
                isLoading={isLoading} />
            </Section>
          </Grid>
        </Grid>
      </Box>

    </PageArea>
  );
}
